<template>
  <div class="tab-group">
    <nav
      class="tab-group__list"
      role="tablist"
      :aria-label="tabGroupTitle"
    >
      <template v-for="(tab, index) in tabsWithId">
        <LinkButton
          :id="`${tab.idPrefix}-button`"
          :key="index"
          tag="button"
          type="button"
          role="tab"
          no-styling
          :aria-selected="activeEntry === index ? 'true' : 'false'"
          :aria-controls="`${tab.idPrefix}-panel`"
          class="tab-group__item"
          :class="{ 'tab-group__item--active': activeEntry === index }"
          @click.stop="setActive(index)"
        >
          {{ tab.label }}
        </LinkButton>
      </template>
    </nav>
    <div
      :class="tabGroupContentClass"
      class="tab-group__content"
    >
      <template v-if="!hasLinks">
        <template v-for="(tab, index) in tabsWithId">
          <div
            :id="`${tab.idPrefix}-panel`"
            :key="index"
            role="tabpanel"
            :aria-hidden="isActive(index, tab) ? 'false' : 'true'"
            :aria-labelledby="`${tab.idPrefix}-button`"
          >
            <div
              v-show="isActive(index, tab)"
              :class="tabEntryClasses(index, tab)"
              class="tab-group__content"
            >
              <slot
                :label="tab.label"
                :name="tab.name"
              />
            </div>
          </div>
        </template>
      </template>
      <template v-else>
        <div>
          <slot />
        </div>
      </template>
    </div>
  </div>
</template>

<script setup lang="ts">
import uniqueId from 'lodash-es/uniqueId';
import { withDefaults, watch, computed, ref } from 'vue';
import { useLazyAsyncData } from '#imports';
import { LinkButton } from '@i22-td-smarthome/component-library';

export type Tab = {
  name: string;
  label: string;
};

export type TabId = Tab & {
  idPrefix: string;
};

export type StageSlideComponentProps = {
  tabs: Tab[];
  active?: number;
  hasLinks?: boolean;
  hasPadding?: boolean;
  tabGroupTitle?: string;
};

const props = withDefaults(defineProps<StageSlideComponentProps>(), {
  active: 0,
  tabGroupTitle: undefined,
});

// replace with "uid" in vue 3
const { data: uid } = useLazyAsyncData<string | undefined>('tab-group', () => uniqueId());

const activeEntry = ref<number>(props.active);

const tabsWithId = computed<TabId[]>(() =>
  props.tabs.map((tab, index) => ({
    ...tab,
    idPrefix: `tab-${uid.value}-${index}`,
  }))
);

watch(
  () => props.active,
  (value) => {
    if (value) {
      activeEntry.value = value;
    }
  }
);

const tabGroupContentClass = computed(() =>
  props.hasPadding ? 'tab-group__content--has-padding' : ''
);

const setActive = (index) => {
  if (activeEntry.value === index) return;
  activeEntry.value = index;
};
const isActive = (index: number, tab) => {
  if (props.hasLinks) {
    return tab.name === activeEntry.value;
  }
  return index === activeEntry.value;
};
const tabEntryClasses = (index, tab) => {
  const selector = `tab-group__content__${tab.name}`;
  const status = isActive(index, tab) ? 'active' : 'inactive';
  return [status, selector];
};
</script>

<style lang="scss" scoped>
@use 'assets/base' as *;

.tab-group {
  &__list {
    width: 100%;
    border-bottom: 1px solid color(grey, 25000);
    text-align: center;
    white-space: nowrap;
    overflow-y: hidden;
    overflow-x: auto;
    -webkit-overflow-scrolling: touch;
    margin-bottom: $grid-base * 2;
  }

  &__item {
    display: inline-block;
    font-weight: 700;
    text-transform: uppercase;
    padding: 5px $grid-base 8px $grid-base;
    border-bottom: 4px solid transparent;
    transition: border 0.125s ease-out;
    border-radius: 0;
    color: currentColor;

    &:hover,
    &--active {
      border-bottom-color: currentColor;
    }

    &--active {
      color: var(--color-primary);
    }

    &:focus-visible {
      outline-offset: -2px;
    }
  }

  &__headline {
    margin-top: 7px;
  }

  &__content {
    margin-bottom: 3 * $grid-base;

    &--has-padding {
      padding-top: 18px;
      @include mq($mq-sm) {
        padding-top: 55px;
      }
    }
  }
}
</style>
